import { Action, ActionEvent, CollectActionEvent, CollectUpdateByDeltaEvent, UpdateByDelta, UpdateByDeltaEvent } from "@/events/events";
import { button } from "./action.button";
import { input } from "./action.input";
import { select } from "./action.select";

export type ActionAttributes = UpdateByDelta | Action;
type WithStore = {
  storeName     : symbol;
  updateComplete: Promise<void>;
};
export type ActionNode<T extends HTMLElement = HTMLElement> = T & WithStore;
export const DATA_ACTION_NODE = "[data-store-action]";

// Track initialized action nodes to ensure we don't wire them up twice
const ACTION_BOUND = new WeakSet<ActionNode>();
const ACTION_LISTENER_DISPOSERS = new WeakMap<HTMLElement, Set<() => void>>();
const ACTION_ORIGINAL_REMOVE = new WeakMap<ActionNode, () => void>();

const shouldCollectForNode = (el: HTMLElement) => !(el.dataset.storeNoCollect === '' || el.dataset.storeNoCollect === 'true');

const disconnectCollectedAction = (el: ActionNode) => {
  if (el instanceof HTMLSelectElement) {
    [...el.options].forEach((option) => disconnectedCallback(option as unknown as ActionNode));
    return;
  }

  disconnectedCallback(el);
};

const teardownActionNode = (node: ActionNode) => {
  const hadState =
    ACTION_BOUND.has(node)
    || ACTION_LISTENER_DISPOSERS.has(node)
    || ACTION_ORIGINAL_REMOVE.has(node);
  if (!hadState) return;

  disconnectCollectedAction(node);

  const listenerDisposers = ACTION_LISTENER_DISPOSERS.get(node);
  if (listenerDisposers) {
    for (const dispose of listenerDisposers) {
      dispose();
    }
    ACTION_LISTENER_DISPOSERS.delete(node);
  }

  const originalRemove = ACTION_ORIGINAL_REMOVE.get(node);
  if (originalRemove) {
    node.remove = originalRemove;
  }

  ACTION_ORIGINAL_REMOVE.delete(node);
  ACTION_BOUND.delete(node);
};

export const readClickAttributes = (el: HTMLElement): ActionAttributes => {
  const ds = el.dataset;
  const storeId = ds.storeId || undefined;

  if ('storeSetType' in ds) {
    return {
      type      : ds.storeSetType as UpdateByDelta["type"],
      delta     : ds.storeSetDelta === "next" || ds.storeSetDelta === "prev" ? ds.storeSetDelta : Number(ds.storeSetDelta),
      min       : Number(ds.storeSetMin) || undefined,
      max       : Number(ds.storeSetMax) || undefined,
      useAsValue: ds.storeSetUseAsValue === 'true' || ds.storeSetUseAsValue === '' || false,
      storeId
    };
  } else {
    return {
      devices     : Number(ds.storeSetDevices) || undefined,
      subscription: Number(ds.storeSetSubscription) || undefined,
      id          : ds.storeSetId,
      campaign    : ds.storeSetCampaign,
      bundle      : ds.storeSetBundle === 'true' || ds.storeSetBundle === '' || false,
      storeId
    };
  }
};

export const dispatchActionAttributes = (el: HTMLElement, attr: ActionAttributes) => {
  if ('type' in attr) {
    el.dispatchEvent(new UpdateByDeltaEvent(attr));
    return;
  }

  el.dispatchEvent(new ActionEvent(attr));
};

const addLogic = async (el: ActionNode) => {
  if (el.updateComplete)  {
    await el.updateComplete;
  }

  if (el instanceof HTMLInputElement) {
    connectedCallback(el);
    input(el);
    return;
  }

  if (el instanceof HTMLSelectElement) {
    [...el.options]
      .forEach(option => connectedCallback(option as unknown as ActionNode));
    select(el);
    return;
  }

  //treat everything else as a button
  button(el);
  connectedCallback(el);
};

export const connectedCallback = (el: ActionNode) => {
  if (!el.storeName) {
    el.storeName = Symbol("action");
  }

  const attr = readClickAttributes(el);
  if (shouldCollectForNode(el)) {
    if ('type' in attr) {
      el.dispatchEvent(new CollectUpdateByDeltaEvent({ name: el.storeName, update: attr }));
    } else {
      el.dispatchEvent(new CollectActionEvent({ name: el.storeName, action: attr }));
    }
  }
};

export const disconnectedCallback = (el: ActionNode) => {
  const attr = readClickAttributes(el);
  if (shouldCollectForNode(el)) {
    if ('type' in attr) {
      el.dispatchEvent(new CollectUpdateByDeltaEvent({
        name  : el.storeName,
        update: null
      }));
    } else {
      el.dispatchEvent(new CollectActionEvent({
        name  : el.storeName,
        action: null
      }));
    }
  }
};

export const handleActionNode = (node: ActionNode) => {
  if (ACTION_BOUND.has(node)) return;
  ACTION_BOUND.add(node);
  addLogic(node);
};

export const cleanupActionNode = (node: ActionNode) => {
  teardownActionNode(node);
};

export const addEventListener = (el: HTMLElement, type: "click" | "change", listener: (event: Event) => void) => {
  el.addEventListener(type, listener);

  let listenerDisposers = ACTION_LISTENER_DISPOSERS.get(el);
  if (!listenerDisposers) {
    listenerDisposers = new Set<() => void>();
    ACTION_LISTENER_DISPOSERS.set(el, listenerDisposers);
  }
  listenerDisposers.add(() => {
    el.removeEventListener(type, listener);
  });

  const actionEl = el as ActionNode;
  if (!ACTION_ORIGINAL_REMOVE.has(actionEl)) {
    const originalRemove = actionEl.remove;
    ACTION_ORIGINAL_REMOVE.set(actionEl, originalRemove);

    actionEl.remove = () => {
      teardownActionNode(actionEl);
      originalRemove.call(actionEl);
    }
  }
}
